import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { requestAsync } from "redux-query";
import App from "./App";
import store from "./store/rootReducer";
import { getSimulatorReservationsByFacilityId } from "modules/simulator-reservations/simulatorReservationQueries";
import Dashboard from "./features/leagues-v2/components/Dashboard";
import { CreateLeaguePage } from "./features/leagues-v2/components/CreateLeaguePage";
import { EditLeaguePage } from "./features/leagues-v2/components/EditLeaguePage";
import { AdjudicationQueuePage } from "./features/leagues-v2/components/AdjudicationQueuePage";
import { LeaguesV2RouteGuard } from "./features/leagues-v2/components/LeaguesV2RouteGuard";
import { AdSpacesRouteGuard } from "./features/adkit/components/AdSpacesRouteGuard";
import { LeaguesV2PathRedirect } from "./features/leagues-v2/components/LeaguesV2PathRedirect";
import { AppCrashPage } from "./components/errors/AppCrashPage";
import { RouteErrorPage } from "./components/errors/RouteErrorPage";
const LeagueDetailPage = lazy(() =>
  import("./features/leagues-v2/components/LeagueDetailPage").then((m) => ({
    default: m.LeagueDetailPage,
  })),
);
const RoundDetailPage = lazy(() =>
  import("./features/leagues-v2/components/RoundDetailPage").then((m) => ({
    default: m.RoundDetailPage,
  })),
);
const RosterImportPage = lazy(() =>
  import("./features/leagues-v2/components/RosterImportPage").then((m) => ({
    default: m.RosterImportPage,
  })),
);
const HandicapHistoryPage = lazy(() =>
  import("./features/leagues-v2/components/handicap/HandicapHistoryPage").then(
    (m) => ({
      default: m.HandicapHistoryPage,
    }),
  ),
);
const EventDetailPage = lazy(() =>
  import("./features/competitions/components/detail/EventDetailPage").then(
    (m) => ({
      default: m.EventDetailPage,
    }),
  ),
);
const SingleFacilityControlDashboard = lazy(
  () => import("components/control/SingleFacilityControlDashboard"),
);
const SettingsContainer = lazy(
  () => import("components/shared/settings/SettingsContainer"),
);
const ClubhouseAdmin = lazy(() => import("components/admin/ClubhouseAdmin"));
const CreateEventPage = lazy(() =>
  import("./features/competitions/components/CreateEventPage").then((m) => ({
    default: m.CreateEventPage,
  })),
);
const EditEventPage = lazy(() =>
  import("./features/competitions/components/EditEventPage").then((m) => ({
    default: m.EditEventPage,
  })),
);
const EventsDashboardPage = lazy(() =>
  import("./features/competitions/components/EventsDashboardPage").then(
    (m) => ({
      default: m.EventsDashboardPage,
    }),
  ),
);
const Marketing = lazy(
  () => import("components/control/marketing-hub/Marketing"),
);
const Reservations = lazy(
  () => import("components/control/simulator-reservations/Reservations"),
);
const LockerRoom = lazy(() => import("./features/locker-room"));
const AdSpacesDashboardPage = lazy(() =>
  import("./features/adkit/components/dashboard/AdSpacesDashboardPage").then(
    (m) => ({ default: m.AdSpacesDashboardPage }),
  ),
);
const NewCampaignPage = lazy(() =>
  import("./features/adkit/components/setup/NewCampaignPage").then((m) => ({
    default: m.NewCampaignPage,
  })),
);
const ImageLibraryPage = lazy(() =>
  import("./features/adkit/components/library/ImageLibraryPage").then((m) => ({
    default: m.ImageLibraryPage,
  })),
);
const CampaignDetailPage = lazy(() =>
  import("./features/adkit/components/detail/CampaignDetailPage").then((m) => ({
    default: m.CampaignDetailPage,
  })),
);

const reservationsLoader = (): null => {
  const facilityId = store.getState().facility?.currentFacilityId;
  if (facilityId) {
    void store.dispatch(
      requestAsync(getSimulatorReservationsByFacilityId(facilityId)),
    );
  }
  return null;
};

export const router = createBrowserRouter([
  {
    element: <App />,
    // The shell itself failing is the only case with no chrome left to render
    // into, so it gets its own bare fallback.
    errorElement: <AppCrashPage />,
    children: [
      {
        // Pathless, so every route below inherits one boundary: a page that
        // throws is replaced inside App's Outlet, leaving the header and
        // sidebar usable. New routes are covered without being listed twice.
        errorElement: <RouteErrorPage />,
        children: [
          { path: "/dashboard", element: <SingleFacilityControlDashboard /> },
          { path: "/settings/:active_tab?", element: <SettingsContainer /> },
          { path: "/admin", element: <ClubhouseAdmin /> },
          { path: "/events", element: <EventsDashboardPage /> },
          { path: "/events/new", element: <CreateEventPage /> },
          { path: "/events/:eventId", element: <EventDetailPage /> },
          { path: "/events/:eventId/edit", element: <EditEventPage /> },
          // Competitions was rebuilt as Events; keep old bookmarks landing there.
          {
            path: "/competitions/*",
            element: <Navigate to="/events" replace />,
          },
          { path: "/marketing", element: <Marketing /> },
          // Leagues used to live under /leagues/v2; keep old bookmarks working.
          { path: "/leagues/v2/*", element: <LeaguesV2PathRedirect /> },
          // The V1 rounds page; the round pages now live under each league.
          {
            path: "/leagues/rounds",
            element: <Navigate to="/leagues" replace />,
          },
          // Leagues V2 is a gated Production Beta — the LeaguesV2RouteGuard redirects
          // to the dashboard when the `leaguesV2` LD flag resolves to false.
          {
            element: <LeaguesV2RouteGuard />,
            children: [
              { path: "/leagues", element: <Dashboard /> },
              {
                path: "/leagues/adjudication",
                element: <AdjudicationQueuePage />,
              },
              { path: "/leagues/new", element: <CreateLeaguePage /> },
              { path: "/leagues/:leagueId", element: <LeagueDetailPage /> },
              {
                path: "/leagues/:leagueId/edit",
                element: <EditLeaguePage />,
              },
              {
                path: "/leagues/:leagueId/rounds/:roundId",
                element: <RoundDetailPage />,
              },
              {
                path: "/leagues/:leagueId/roster/import",
                element: <RosterImportPage />,
              },
              {
                path: "/leagues/:leagueId/roster/:memberId/handicap-history",
                element: <HandicapHistoryPage />,
              },
            ],
          },
          {
            path: "/reservations",
            element: <Reservations />,
            loader: reservationsLoader,
          },
          { path: "/lockerroom", element: <LockerRoom /> },
          { path: "/ad-spaces", element: <AdSpacesDashboardPage /> },
          // Setup and the Image Library need the facility's Ad Spaces
          // entitlement; the guard redirects to the list without it.
          {
            element: <AdSpacesRouteGuard />,
            children: [
              { path: "/ad-spaces/new", element: <NewCampaignPage /> },
              { path: "/ad-spaces/images", element: <ImageLibraryPage /> },
            ],
          },
          {
            path: "/ad-spaces/:campaignId",
            element: <CampaignDetailPage />,
          },
          {
            path: "/adkit",
            element: <Navigate to="/ad-spaces" replace />,
          },
          { path: "/", element: <Navigate to="/dashboard" replace /> },
          { path: "*", element: <Navigate to="/dashboard" replace /> },
        ],
      },
    ],
  },
]);
